
import { Redirect, Route, Switch } from 'react-router-dom';
import { IonReactHashRouter } from '@ionic/react-router';
import { AppUrlListener } from './AppUrlListener.tsx';
import { PushNotificationListener } from '../push-notifications/PushNotificationListener.tsx';
import { DebugPage } from '../support/debug/DebugPage.tsx';
import { MissionDetailsPage } from '../mission/MissionDetailsPage.tsx';
import { MissionListPage } from '../missions/MissionListPage.tsx';
import { MapPage } from '../map/MapPage.tsx';
import { MissionMapPage } from '../map/MissionMapPage.tsx';
import { useInstance } from '../support/ioc/useInstance';
import { HistoryStorage, HistoryViewModel } from './HistoryViewModel.ts';
import { IonRouterOutlet } from '@ionic/react';
import { GlobalPage } from './GlobalPage.tsx';
import { InvalidMissionPage } from '../mission/InvalidMissionPage.tsx';
import { ProfilePage } from '../profile/ProfilePage.tsx';
import { OperatorState } from '../profile/OperatorState.ts';
import { SetupPage } from '../setup/SetupPage.tsx';
import { useSubscription } from '@autopylot-internal/state';
import { LogsPage } from '../support/debug/LogsPage.tsx';
import { ErrorPage } from './ErrorPage.tsx';
import { DefaultPage } from './DefaultPage.tsx';
import { AutoPylotPrincipal } from '../auth/AutoPylotPrincipal.ts';
import { LoadingPage } from './LoadingPage.tsx';
import { MissionWeatherPage } from '../weather/MissionWeatherPage.tsx';
import { WeatherMapPage } from '../weather/WeatherMapPage.tsx';
import { MissionNotesPage } from '../mission/notes/MissionNotesPage.tsx';
import { HourlyWeatherChartPage } from '../weather/chart/HourlyWeatherChartPage.tsx';
import { InsuranceLandingPage } from '../insurance/InsuranceLandingPage.tsx';
import { InvitePage } from '../invite/InvitePage.tsx';
import { CalendarPage } from '../calendar/CalendarPage.tsx';
import { MissionChecklistPage } from '../mission/checklist/MissionChecklistPage.tsx';
import { SavedChecklistsPage } from '../checklists/SavedChecklistsPage.tsx';
import { SavedChecklistPage } from '../checklists/SavedChecklistPage.tsx';
import { CopySavedChecklistsPage } from '../checklists/CopySavedChecklistsPage.tsx';
import React, { ReactNode, useEffect } from 'react';
import { LiveUpdatesListener } from '../support/live-updates/LiveUpdatesListener.tsx';
import { MissionAuthorizationDetailsPage } from '../mission/MissionAuthorizationDetailsPage.tsx';
import { RenderChildrenWhenMapsLoaded } from '../map/RenderChildrenWhenMapsLoaded.tsx';
import { FAAStatusAlerter } from './FAAStatusAlerter.tsx';
import { SettingsPage } from '../menu/SettingsPage.tsx';
import { AppStartTimings } from '../support/debug/AppStartTimings.ts';
import { Logger } from '../support/debug/Logger.ts';
import { DismissSplashScreen } from './DismissSplashScreen.tsx';
import { DeviceStatusBar } from '../support/DeviceStatusBar.ts';
import { LiveUpdatesManager } from '../support/live-updates/LiveUpdatesManager.tsx';
import { AppVersionUpdateAlerter } from './app-updates/AppVersionUpdateAlerter.tsx';
import { useIsNative } from '../support/hooks/useIsNative.tsx';
import { Unsupported } from './Unsupported.ts';
import { UnsupportedPage } from './UnsupportedPage.tsx';
import { AuthErrorAlerter } from '../auth/AuthErrorAlerter.ts';
import { BindMapViewModel } from '../map/components/BindMapViewModel.tsx';
import { BindWindyMapViewModel } from '../weather/windy/BindWindyMapViewModel.tsx';
import { RevenueCatDebugPage } from '../paywall/revenue-cat/RevenueCatDebugPage.tsx';
import { PaywallDialog } from '../paywall/PaywallDialog.tsx';
import { AnnouncementDialog } from '../components/dialog/AnnouncementDialog.tsx';
import { WindyMapModal } from '../weather/windy/WindyMapModal.tsx';
import { DXMissionReport } from '../reports/DXMissionReport.tsx';
import { MarketplacePage } from '../affiliates/MarketplacePage.tsx';
import { FleetManagementPage } from '../fleet/FleetManagementPage.tsx';
import { AircraftDetailsPage } from '../fleet/AircraftDetailsPage.tsx';
import { EnsureFleetLoaded } from '../fleet/EnsureFleetLoaded.tsx';
import { AircraftLogPage } from '../fleet/AircraftLogPage.tsx';
import { AircraftLogEntryPage } from '../fleet/AircraftLogEntryPage.tsx';
import { TechnicalInfoPage } from '../support/debug/TechnicalInfoPage.tsx';

// swallows computeMatch prop from IonRouterOutlet, suppressing the react error
const ComputeMatchSink: React.FC<{computeMatch?: any, children: ReactNode}> = ({children}) => children;

export const AppRouter = () => {
    const historyStorage = useInstance(HistoryStorage);
    const timings = useInstance(AppStartTimings);
    const logger = useInstance(Logger);
    const statusBar = useInstance(DeviceStatusBar);
    const unsupported = useInstance(Unsupported);

    // <Route> elements must be direct children of <Routes>, so use
    // boolean conditional around each one instead of <IfNative>
    const isNative = useIsNative();

    const { isAdmin } = useInstance(AutoPylotPrincipal);

    const historyVM = useInstance(HistoryViewModel);

    const operatorState = useInstance(OperatorState);
    useSubscription(() => operatorState);

    // if app start path is in local storage, then app is restarting
    // (presumably after installing an update) and we need to navigate
    useEffect(() => {
        timings.add('<AppRouter />');
        logger.info('app start timings', timings.getAll());

        const startPath = historyStorage.appStartPath.get();
        if (startPath) {
            historyStorage.appStartPath.remove();
            historyVM.replace(startPath);
        }
    }, [historyStorage, historyVM, timings, logger]);

    const needsSetup = operatorState.needsSetup();

    if (unsupported.isUnsupported()) {
        return <UnsupportedPage />;
    }

    return (
        <IonReactHashRouter history={historyVM.getRawHistory()}>
            <LiveUpdatesListener />
            <AppUrlListener />
            <PushNotificationListener />

            <AuthErrorAlerter />

            <GlobalPage>
                <IonRouterOutlet key={needsSetup ? 'setup' : 'app'} id="main-outlet">
                    <ComputeMatchSink>
                        {needsSetup &&
                            <DismissSplashScreen statusBar={statusBar}>
                                <SetupPage />
                            </DismissSplashScreen>
                        }
                        {!needsSetup && (<>
                            <BindMapViewModel />
                            <BindWindyMapViewModel />

                            <PaywallDialog />
                            <AnnouncementDialog />

                            <WindyMapModal />

                            <RenderChildrenWhenMapsLoaded>
                                <DismissSplashScreen statusBar={statusBar}>

                                    <FAAStatusAlerter />
                                    <AppVersionUpdateAlerter />
                                    {/* // NOTE: IonRouterOutlet route selection is very confusing and so we are using Switch. (see notes in footer of this file). */}
                                    <Switch>

                                        {/* need to support this deprecated route with a redirect for a while,
                                        to handle app restart after live update that replaces it with /map */}
                                        <Route exact path="/missions/new">
                                            <Redirect to="/map" />
                                        </Route>

                                        <Route exact path="/map" render={() => (
                                            <MapPage />
                                        )} />
                                        <Route exact path="/missions/invalid" render={() => (
                                            <InvalidMissionPage />
                                        )} />
                                        <Route exact path="/missions/:missionId" render={(props) => (
                                            <MissionDetailsPage missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions/:missionId/dx/report" render={(props) => (
                                            <DXMissionReport missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions/:missionId/map" render={(props) => (
                                            <MissionMapPage missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions/:missionId/weather" render={(props) => (
                                            <MissionWeatherPage missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions/:missionId/checklist" render={(props) => (
                                            <MissionChecklistPage missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions/:missionId/notes" render={(props) => (
                                            <MissionNotesPage missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions/:missionId/laanc-authorization" render={(props) => (
                                            <MissionAuthorizationDetailsPage missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions/:missionId/weather/map" render={(props) => (
                                            <WeatherMapPage missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions/:missionId/weather/hourly" render={(props) => (
                                            <HourlyWeatherChartPage missionId={props.match.params.missionId} />
                                        )} />
                                        <Route exact path="/missions">
                                            <MissionListPage />
                                        </Route>

                                        <Route exact path="/checklists">
                                            <SavedChecklistsPage />
                                        </Route>
                                        <Route exact path="/checklists/copy">
                                            <CopySavedChecklistsPage />
                                        </Route>
                                        <Route exact path="/checklists/:checklistId" render={(props) => (
                                            <SavedChecklistPage checklistId={props.match.params.checklistId} />
                                        )} />

                                        <Route exact path="/profile">
                                            <ProfilePage />
                                        </Route>
                                        <Route exact path="/settings/profile">
                                            <ProfilePage />
                                        </Route>

                                        <Route exact path="/insurance">
                                            <InsuranceLandingPage />
                                        </Route>

                                        <Route path="/fleet">
                                            <EnsureFleetLoaded>
                                                <Route exact path="/fleet" render={() => (
                                                    <FleetManagementPage />
                                                )} />
                                                <Route exact path="/fleet/:aircraftId" render={(props) => (
                                                    <AircraftDetailsPage aircraftId={props.match.params.aircraftId} />
                                                )} />
                                                <Route exact path="/fleet/:aircraftId/log" render={(props) => (
                                                    <AircraftLogPage aircraftId={props.match.params.aircraftId} />
                                                )} />
                                                <Route exact path="/fleet/:aircraftId/log/:noteId" render={(props) => (
                                                    <AircraftLogEntryPage aircraftId={props.match.params.aircraftId} noteId={props.match.params.noteId} />
                                                )} />
                                            </EnsureFleetLoaded>
                                        </Route>

                                        {isNative &&
                                            <Route exact path="/invite">
                                                <InvitePage />
                                            </Route>
                                        }
                                        {isNative &&
                                            <Route exact path="/calendar">
                                                <CalendarPage />
                                            </Route>
                                        }

                                        <Route exact path="/settings">
                                            <SettingsPage />
                                        </Route>
                                        <Route exact path="/error">
                                            <ErrorPage error={new Error('Test error message')} />
                                        </Route>
                                        <Route exact path="/loading">
                                            <LoadingPage />
                                        </Route>
                                        {isAdmin && (
                                            <Route exact path="/debug">
                                                <DebugPage />
                                            </Route>
                                        )}
                                        <Route exact path="/tech-info">
                                            <TechnicalInfoPage />
                                        </Route>
                                        {isAdmin && (
                                            <Route exact path="/debug-paywall">
                                                <RevenueCatDebugPage />
                                            </Route>
                                        )}
                                        {isAdmin && (
                                            <Route exact path="/logs">
                                                <LogsPage />
                                            </Route>
                                        )}
                                        {isAdmin && (
                                            <Route path="/live-updates-manager">
                                                <LiveUpdatesManager />
                                            </Route>
                                        )}
                                        <Route path="/marketplace">
                                            <MarketplacePage />
                                        </Route>
                                        <Route path="/oauth/code">
                                            {/* placeholder to enable LiveUpdatesListener to skip while app reloads */}
                                            {null}
                                        </Route>
                                        <Route exact path="/">
                                            <DefaultPage />
                                        </Route>
                                        <Route>
                                            {/* fallback for all 404s */}
                                            <Redirect to="/" />
                                        </Route>
                                    </Switch>
                                </DismissSplashScreen>
                            </RenderChildrenWhenMapsLoaded>
                        </>)}
                    </ComputeMatchSink>
                </IonRouterOutlet>
            </GlobalPage>
        </IonReactHashRouter >
    );
};

/**
 * What was confusing about routing with IonRouterOutlet
 *      - typically we write our routes like this
 *          - /missions/invalid
 *          - /missions/:missionId
 *          - (none) => redirect
 *     - which achieves 2 things,
 *        - /missions/invalid matches first, so it doesn't hit /missions/:missionId
 *          - if none of them match, we can redirect, or 404
 *     - we get that from react-router <Switch>
 *     - but that is not how IonRouterOutlet works...
 *          - on page load it chooses /missions/invalid
 *          - on hash change it choose /missions/:missionId where missionId is `invalid`
 *          - WTF!
 *     - it also renders in an inconsistent state when changing hash (eg going from mission id 1 to 2, it will say match param is 1, on path /missions/2 ). Likely not an issue, and likely true down in react-router but could cause obscure bugs.
 *     - also, there is no way to have a default / fallback route (eg to redirect to a more useful page, or render a 404 with a link to a more useful page).
 *     - it has no way of doing a default route, and the prescribed way is with a switch
 *     - I then found this thread reinforcing my findings https://forum.ionicframework.com/t/react-router-default-route/199024/6
 */
